import { Injectable } from '@angular/core';

import {
  normalizeDateValue,
  normalizeFilterIds,
  normalizeStringValue,
  normalizeUnitFilterValues,
  objectToQueryString,
} from '../../common/utils';

import { environment } from '../../../environments/environment';

import { RestService } from './rest.service';

export interface IndividualSearchFilters {
  ballotTypeIds?: number[] | null;
  importId?: number | string | null;
  eligible?: boolean | null;
  includeUnassigned?: boolean;
  localIds?: number[] | null;
  regions?: string[] | null;
  search?: string;
  units?: string[] | null;
}

export interface ChangeOfAddressReportFilters {
  endDate?: any;
  startDate?: any;
}

@Injectable()
export class IndividualService {
  constructor(private readonly rest: RestService) {}

  getIndividuals(searchOrFilters: string | IndividualSearchFilters = ''): Promise<any> {
    const query = objectToQueryString(this.normalizeSearchFilters(searchOrFilters));

    return this.rest.get(`${environment.apiUrl}/api/core/individual${query}`);
  }

  getIndividualById(id: number): Promise<any> {
    return this.rest.get(`${environment.apiUrl}/api/core/individual/${id}`);
  }

  getIndividualStatusHistory(id: number): Promise<any> {
    return this.rest.get(`${environment.apiUrl}/api/core/individual/${id}/status-history`);
  }

  getChangeOfAddressReport(filters: ChangeOfAddressReportFilters): Promise<any> {
    const query = objectToQueryString({
      endDate: normalizeDateValue(filters ? filters.endDate : null),
      startDate: normalizeDateValue(filters ? filters.startDate : null),
    });

    return this.rest.get(`${environment.apiUrl}/api/core/individual/reports/!changeOfAddress${query}`);
  }

  createIndividual(payload: any): Promise<any> {
    return this.rest.post(`${environment.apiUrl}/api/core/individual`, payload);
  }

  importIndividual(payload: any): Promise<any> {
    return this.rest.post(`${environment.apiUrl}/api/core/individual/!import`, payload);
  }

  validateImport(payload: any): Promise<any> {
    return this.rest.post(`${environment.apiUrl}/api/core/individual/!validateImport`, payload);
  }

  updateIndividual(id: number, payload: any): Promise<any> {
    return this.rest.put(`${environment.apiUrl}/api/core/individual/${id}`, payload);
  }

  deleteIndividual(id: number): Promise<any> {
    return this.rest.delete(`${environment.apiUrl}/api/core/individual/${id}`);
  }

  getDeletedIndividuals(localIds: number[] = []): Promise<any> {
    const query = objectToQueryString({
      localIds: normalizeFilterIds(localIds),
    });

    return this.rest.get(`${environment.apiUrl}/api/core/individual/!deleted${query}`);
  }

  restoreIndividuals(individualIds: number[]): Promise<any> {
    return this.rest.post(`${environment.apiUrl}/api/core/individual/!bulkRestore`, {
      individualIds,
    });
  }

  deleteIndividuals(individualIds: number[]): Promise<any> {
    return this.rest.post(`${environment.apiUrl}/api/core/individual/!bulkDelete`, {
      individualIds,
    });
  }

  private normalizeSearchFilters(searchOrFilters: string | IndividualSearchFilters): IndividualSearchFilters {
    const filters = typeof searchOrFilters === 'string' ? { search: searchOrFilters } : searchOrFilters || {};

    return {
      ballotTypeIds: normalizeFilterIds(filters.ballotTypeIds),
      importId: normalizeStringValue(filters.importId),
      eligible: typeof filters.eligible === 'boolean' ? filters.eligible : null,
      includeUnassigned: !!filters.includeUnassigned,
      localIds: normalizeFilterIds(filters.localIds),
      regions: normalizeUnitFilterValues(filters.regions),
      search: normalizeStringValue(filters.search),
      units: normalizeUnitFilterValues(filters.units),
    };
  }
}
